'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import DownArrowIcon from '@/assets/images/down-arrow.svg';
import UpArrowIcon from '@/assets/images/up-arrow.svg';
import { DMButton } from '@/components/button';
import { DMImage } from '@/components/custom-image/custom-image';
import { SkeletonLoader } from '@/components/skeleton-loader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
  convertMinutesToHourMinute,
  convertSecondsToMinOrHours,
  convertSecondsToTimeString,
  countStats,
} from '@/lib/utils';
import { useCourseStore } from '@/lib/zustand/store/store';
import { FileTextIcon, Lock, LockIcon, Play, PlayCircle } from 'lucide-react';
import { useSession } from 'next-auth/react';

function CourseContent() {
  const [openItem, setOpenItem] = useState<string[]>([]);
  const { playList, setData } = useCourseStore(state => state);
  const router = useRouter();
  const { data }: any = useSession();

  useEffect(() => {
    if (playList?.chapters) {
      const allItems = playList.chapters.map((_: any, i: number) => `item-${i}`);
      setOpenItem(allItems);
    }
  }, [playList]);

  const handleExpandDetails = () => {
    if (openItem.length === 0) {
      const allItems = playList?.chapters.map((_: any, i: number) => `item-${i}`);
      setOpenItem(allItems);
    } else {
      setOpenItem([]);
    }
  };

  const toggleItem = (value: string) => {
    setOpenItem(current => {
      if (current.includes(value)) {
        return current.filter(item => item !== value);
      }
      return [...current, value];
    });
  };

  const handleLectureClick = (e: any, chapterId: number, lecture: any) => {
    e.preventDefault();
    setData({
      url: lecture.link,
      type: lecture.type,
      chapterId: chapterId,
      lectureId: lecture.id,
      lectureTitle: lecture.title,
      isCompleted: lecture.completedLecture
        ? lecture?.completedLecture?.length != 0 &&
          lecture?.completedLecture[0]?.lectureId == lecture?.id
        : false,
    });
    router.push(`/courses/${playList.slug}/${lecture.slug}`);
  };

  const renderContent = (type: any) => {
    switch (type) {
      case 'video':
        return (
          <div className='flex justify-between space-x-3 items-center m-1'>
            <PlayCircle className='h-5 w-5 text-slate-400' />
          </div>
        );
      case 'lock':
        return <LockIcon />;
      case 'task':
        return (
          <div className='flex justify-between space-x-3 items-center m-1'>
            <FileTextIcon className='h-5 w-5 text-slate-400' />
          </div>
        );
      default:
        return null;
    }
  };

  console.log(playList?.chapters, '===item');

  return (
    <div className='mt-5 md:mt-14 flex justify-between flex-col'>
      <div className='flex justify-between flex-col md:flex-row gap-2'>
        <div className=''>
          <h3 className='text-2xl'>Course content</h3>
          <p className='sub-description mt-3'>
            {/* 14 sections 94 lectures 13h 29m total length */}
            {`${countStats(playList).totalChapters} Chapters ${countStats(playList).totalLectures} Lectures, Total time ${convertSecondsToMinOrHours(countStats(playList).totalDuration)}`}
          </p>
        </div>
        <div className='mt-2 flex justify-end'>
          <DMButton
            variant='secondary'
            onClick={handleExpandDetails}
            className='flex items-center gap-1 rounded-md text-sm !px-5 !py-3 max-sm:hidden'
          >
            {openItem.length !== 0 ? 'Collapse all section' : 'Expand all section'}
            <DMImage
              src={openItem.length === 0 ? DownArrowIcon : UpArrowIcon}
              alt='Home Icon'
              width={18}
              height={18}
            />
          </DMButton>
        </div>
      </div>
      <Accordion
        type='multiple'
        className='w-full mt-3 md:mt-8'
        value={openItem}
        onValueChange={() => setOpenItem}
      >
        {playList?.chapters?.length > 0 ? (
          playList?.chapters?.map((item: any, index: any) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger
                className='bg-brand-secondary rounded-lg p-6  text-sm md:text-md text-left'
                onClick={() => toggleItem(`item-${index}`)}
              >
                {item.title}
              </AccordionTrigger>
              <AccordionContent className='bg-brand-secondary p-3 md:p-6 rounded-b-lg text-sm md:text-lg leading-6 font-normal mb-2'>
                {item?.lectures?.map((lecture: any, index: number) => (
                  <div className='flex gap-2 justify-between items-center my-2' key={index}>
                    <div
                      //href={`/courses/${item.course_id}/${id}`}
                      onClick={e => handleLectureClick(e, item.id, lecture)}
                      className='text-white w-full p-1 flex gap-4 items-center text-base hover:text-brand-primary hover:bg-slate-800 rounded-md cursor-pointer sm:px-3'
                    >
                      <div>
                        {lecture?.link && lecture?.status !== 'lock' && (
                          <Play className='text-brand-silver w-5 h-5' />
                        )}
                        {lecture?.status === 'lock' && (
                          <Lock className='text-brand-silver w-5 h-5' />
                        )}

                        {/* {lecture?.type == 'task' && lecture?.status !== 'lock' && (
                          <Lock className='text-brand-silver w-5 h-5' />
                        )} */}
                        {lecture?.type == 'task' && lecture?.status !== 'lock' && (
                          <FileTextIcon className='h-5 w-5 text-brand-silver' />
                        )}
                      </div>
                      <div className='flex text-base flex-1 items-center lg:items-center gap-4'>
                        {/* {renderContent(lecture.type)} */}
                        <span className='text-sm'>{lecture.title}</span>
                      </div>
                      <div className='text-sm w-1/6 text-right hidden md:block'>
                        {lecture?.type === 'task' && lecture?.status === 'lock' && (
                          <FileTextIcon className='h-5 w-5 ml-auto text-brand-silver' />
                        )}
                        {lecture?.type === 'task' && lecture?.status !== 'lock' && (
                          <FileTextIcon className='h-5 w-5 ml-auto text-brand-primary' />
                        )}

                        {lecture?.type === 'video' &&
                          lecture.lockForGuest &&
                          convertSecondsToMinOrHours(lecture?.duration, 'short')}

                        {lecture?.type === 'video' && !lecture?.lockForGuest && (
                          <Badge className='border-brand-primary text-brand-primary px-3 py-1'>
                            Preview
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))
        ) : (
          <SkeletonLoader count={2} height={80} className='mb-2' />
        )}
      </Accordion>
    </div>
  );
}

export default CourseContent;
